import {
  QueueActivityLogTeam,
  QueueActivityLogUser,
  ReadQueueActivityLogApiResponse,
} from 'lib-shared-types-api';

interface QueueUnrestrictedLog extends QueueActivityBase {
  type: 'QueueDerestricted';
}

interface QueueRestrictedLog extends QueueActivityBase {
  type: 'QueueRestricted';
}

interface AgentRemovedLog extends QueueActivityBase {
  type: 'AgentRemovedFromQueue';
  agentId: string;
  agentName: string;
}

interface AgentAddedLog extends QueueActivityBase {
  type: 'AgentAddedToQueue';
  agentId: string;
  agentName: string;
  isViewOnly?: boolean;
}

interface AgentUpdatedLog extends QueueActivityBase {
  type: 'AgentUpdatedInQueue';
  agentId: string;
  agentName: string;
  isViewOnly: boolean;
}

interface TeamAddedLog extends QueueActivityBase {
  type: 'TeamAddedToQueue';
  teamId: string;
  teamName: string;
  isViewOnly?: boolean;
}

interface TeamRemovedLog extends QueueActivityBase {
  type: 'TeamRemovedFromQueue';
  teamId: string;
  teamName: string;
}

interface TeamUpdatedLog extends QueueActivityBase {
  type: 'TeamUpdatedInQueue';
  teamId: string;
  teamName: string;
  isViewOnly: boolean;
}

interface UnknownLog extends QueueActivityBase {
  type: string;
}

type QueueActivityBase = {
  id: string;
  timestamp: string;
  organizationId: string;
  authorId: string;
  authorName: string;
};

export type QueueActivityLog =
  | QueueUnrestrictedLog
  | QueueRestrictedLog
  | AgentAddedLog
  | AgentRemovedLog
  | TeamRemovedLog
  | TeamAddedLog
  | UnknownLog;

export type QueueActivity = {
  activityLog: QueueActivityLog[];
  lastKey?: string;
};

export const transformReadQueueActivityLogResponse = (
  response: ReadQueueActivityLogApiResponse,
): QueueActivity => {
  const users: {
    [userId: QueueActivityLogUser['id']]: QueueActivityLogUser['name'];
  } = response.data.users.reduce(
    (acc, user) => ({ ...acc, [user.id]: user.name }),
    {},
  );

  const teams: {
    [teamId: QueueActivityLogTeam['id']]: QueueActivityLogTeam['name'];
  } = response.data.teams.reduce(
    (acc, team) => ({
      ...acc,
      [team.id]: team.name,
    }),
    {},
  );

  return {
    activityLog: response.data.audit_log.map((log): QueueActivityLog => {
      const base: QueueActivityBase = {
        id: log.id,
        authorId: log.author_id,
        authorName: users[log.author_id] || '',
        organizationId: log.organization_id,
        timestamp: log.timestamp,
      };

      const eventPayload = JSON.parse(log.event.payload);

      switch (log.event.type) {
        case 'QueueDerestricted':
        case 'QueueRestricted': {
          return { ...base, type: log.event.type };
        }
        case 'AgentAddedToQueue':
        case 'AgentRemovedFromQueue':
        case 'AgentUpdatedInQueue': {
          const agentId: string = eventPayload.agent_id || '';
          const agentName: string = users[agentId] || '';
          return {
            ...base,
            type: log.event.type,
            agentId,
            agentName,
            isViewOnly: eventPayload.is_view_only,
          };
        }
        case 'TeamAddedToQueue':
        case 'TeamRemovedFromQueue':
        case 'TeamUpdatedInQueue': {
          const teamId: string = eventPayload.team_id || '';
          const teamName: string = teams[teamId] || '';

          return {
            ...base,
            type: log.event.type,
            teamId,
            teamName,
            isViewOnly: eventPayload.is_view_only,
          };
        }
        default:
          return { ...base, type: log.event.type };
      }
    }),
    lastKey: response.meta.last_key,
  };
};

export function isTeamRemovedLog(log: QueueActivityLog): log is TeamRemovedLog {
  return log.type === 'TeamRemovedFromQueue';
}

export function isTeamAddedLog(log: QueueActivityLog): log is TeamAddedLog {
  return log.type === 'TeamAddedToQueue';
}

export function isTeamUpdatedLog(log: QueueActivityLog): log is TeamUpdatedLog {
  return log.type === 'TeamUpdatedInQueue';
}

export function isAgentRemovedLog(
  log: QueueActivityLog,
): log is AgentRemovedLog {
  return log.type === 'AgentRemovedFromQueue';
}

export function isAgentAddedLog(log: QueueActivityLog): log is AgentAddedLog {
  return log.type === 'AgentAddedToQueue';
}

export function isAgentUpdatedLog(log: QueueActivityLog): log is AgentUpdatedLog {
  return log.type === 'AgentUpdatedInQueue';
}
